import { useState, useEffect } from "react";
import { Outlet, NavLink, Link, useNavigate, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { allowedNavPaths, isAdmin as isAdminRole, isDoctor, ROLE_LABELS } from "@/lib/roles";
import axios from "axios";
import {
  LayoutDashboard, FlaskConical, MapPin, Users, FileText,
  ClipboardList, MessageSquare, Shield,
  UserCog, ChevronLeft, ChevronRight, LogOut, Bell, Menu,
  FileBox, Gavel, Activity
} from "lucide-react";
import { AmdirisLogo } from "@/components/AmdirisLogo";
import { MandatoryAgreementGate, NotificationBell } from "@/components/AgreementGate";
import { ThemeToggle } from "@/components/ThemeToggle";

import { API } from "@/lib/api-base";

// Cache resolved study names so we don't refetch on every render
const studyNameCache = new Map();

const navGroups = [
  {
    label: "Overview",
    items: [{ path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" }]
  },
  {
    label: "Studies",
    items: [
      { path: "/studies", icon: FlaskConical, label: "Studies" },
      { path: "/sites", icon: MapPin, label: "Sites" },
    ]
  },
  {
    label: "Data Management",
    items: [
      { path: "/ecrf-builder", icon: FileText, label: "eCRF Builder" },
      { path: "/queries", icon: MessageSquare, label: "Queries" },
    ]
  },
  {
    label: "Compliance",
    items: [
      { path: "/audit-trail", icon: Shield, label: "Audit Trail" },
      { path: "/validation", icon: FileBox, label: "Validation Docs" },
    ]
  },
  {
    label: "Admin",
    items: [
      { path: "/users", icon: UserCog, label: "User Management" },
      { path: "/subjects", icon: Users, label: "Subject Management", superAdminOnly: true },
      { path: "/system-health", icon: Activity, label: "System Health", superAdminOnly: true },
    ],
    adminOnly: true
  }
];

// Build the doctor/investigator/sponsor minimal sidebar for their bound study
function buildDoctorNav(studyId, role) {
  if (!studyId) return [];
  const base = `/studies/${studyId}`;
  if (role === "sponsor") {
    return [
      { path: `${base}?tab=overview`, icon: LayoutDashboard, label: "Dashboard", queryKey: "tab", queryValue: "overview" },
      { path: `${base}?tab=investigators`, icon: Users, label: "Investigators / Sites", queryKey: "tab", queryValue: "investigators" },
      { path: `${base}?tab=subjects`, icon: Users, label: "Subjects", queryKey: "tab", queryValue: "subjects" },
      { path: `${base}?tab=dataentry`, icon: ClipboardList, label: "Data Entry", queryKey: "tab", queryValue: "dataentry" },
    ];
  }
  return [
    { path: `${base}?tab=overview`, icon: LayoutDashboard, label: "My Study", queryKey: "tab", queryValue: "overview" },
    { path: `${base}?tab=subjects`, icon: Users, label: "Patients", queryKey: "tab", queryValue: "subjects" },
    { path: `${base}?tab=dataentry`, icon: ClipboardList, label: "Data Entry", queryKey: "tab", queryValue: "dataentry" },
    { path: `${base}?tab=protocols`, icon: FileBox, label: "Protocol", queryKey: "tab", queryValue: "protocols" },
    { path: `${base}?tab=ethics`, icon: Gavel, label: "Ethics", queryKey: "tab", queryValue: "ethics" },
  ];
}

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [doctorStudy, setDoctorStudy] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile drawer when route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // For doctor/investigator/sponsor: fetch their bound study (single-study isolation)
  useEffect(() => {
    const role = user?.role;
    if (!isDoctor(role) && role !== "investigator" && role !== "sponsor") return;
    axios.get(`${API}/studies`, { withCredentials: true })
      .then(r => {
        const s = (r.data || [])[0];
        if (s) {
          setDoctorStudy(s);
          // If user just logged in (still on /dashboard or /), redirect to their study
          if (["/", "/dashboard", "/studies"].includes(location.pathname)) {
            navigate(`/studies/${s._id}?tab=overview`, { replace: true });
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [user?.role]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isAdmin = isAdminRole(user?.role);
  const isDocRole = isDoctor(user?.role) || user?.role === "investigator";
  const isSponsorRole = user?.role === "sponsor";
  const useStudyNav = isDocRole || isSponsorRole;
  const allowed = allowedNavPaths(user?.role); // null = all
  const isPathAllowed = (p) => allowed === null || allowed.includes(p);

  const doctorNav = useStudyNav ? buildDoctorNav(doctorStudy?._id, user?.role) : null;
  const currentTab = new URLSearchParams(location.search).get("tab");

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "linear-gradient(135deg, #080F1E 0%, #0A1628 100%)" }}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)" }}
             onClick={() => setMobileOpen(false)} data-testid="mobile-sidebar-overlay" />
      )}
      {/* Sidebar */}
      <aside
        className={`flex flex-col transition-all duration-300 flex-shrink-0 fixed lg:static z-50 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{
          width: collapsed ? 64 : 240,
          background: "rgba(10,22,40,0.97)",
          borderRight: "1px solid rgba(255,255,255,0.07)",
          backdropFilter: "blur(20px)",
          // Use dvh so the mobile browser URL bar / address bar
          // does NOT push the bottom Logout button off-screen.
          height: "100dvh",
          maxHeight: "100dvh",
          overflow: "hidden",
          paddingBottom: "env(safe-area-inset-bottom)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* Logo */}
        <div className={`relative border-b border-white/5 ${collapsed ? "px-2 py-4 flex flex-col items-center gap-2" : "px-4 py-5 flex items-center gap-3"}`}>
          <AmdirisLogo size={collapsed ? 32 : 36} />
          {!collapsed && (
            <span className="font-semibold text-white text-lg" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.02em" }}>
              AMDiris
            </span>
          )}
          {/* Toggle: when expanded, sits inline (ml-auto). When collapsed, becomes a centered full-width button below the logo so it can never be clipped. */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`text-slate-400 hover:text-white transition-colors rounded hidden lg:flex items-center justify-center ${collapsed ? "w-9 h-7 mt-1 border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-400/5" : "ml-auto p-1"}`}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          {doctorNav ? (
            <div className="mb-3">
              {!collapsed && (
                <p className="text-xs uppercase tracking-widest text-slate-600 px-3 mb-1 font-medium">My Workspace</p>
              )}
              {doctorNav.map((item, idx) => {
                const highlight = currentTab
                  ? item.queryValue === currentTab
                  : idx === 0;
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                    className={`sidebar-nav-item ${highlight ? "active" : ""}`}
                    title={collapsed ? item.label : ""}
                  >
                    <item.icon size={18} strokeWidth={1.5} className="flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ) : (
            navGroups.map((group) => {
              if (group.adminOnly && !isAdmin) return null;
              const isSuperAdmin = user?.role === "super_admin";
              const items = group.items
                .filter(i => !i.superAdminOnly || isSuperAdmin)
                .filter(i => isPathAllowed(i.path));
              if (items.length === 0) return null;
              return (
                <div key={group.label} className="mb-3">
                  {!collapsed && (
                    <p className="text-xs uppercase tracking-widest text-slate-600 px-3 mb-1 font-medium">
                      {group.label}
                    </p>
                  )}
                  {items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')}`}
                      className={({ isActive }) =>
                        `sidebar-nav-item ${isActive ? "active" : ""}`
                      }
                      title={collapsed ? item.label : ""}
                    >
                      <item.icon size={18} strokeWidth={1.5} className="flex-shrink-0" />
                      {!collapsed && <span>{item.label}</span>}
                    </NavLink>
                  ))}
                </div>
              );
            })
          )}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-white/5">
          {!collapsed ? (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                   style={{ background: "linear-gradient(135deg, #00B4D8, #14B8A6)" }}>
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">{ROLE_LABELS[user?.role] || user?.role?.replace("_", " ")}</p>
              </div>
              <button onClick={handleLogout} className="text-slate-400 hover:text-red-400 transition-colors p-1"
                      data-testid="logout-btn" title="Logout">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button onClick={handleLogout} className="w-full flex justify-center text-slate-400 hover:text-red-400 transition-colors p-2"
                    data-testid="logout-btn-collapsed">
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden w-full">
        {/* Top Header */}
        <header className="flex items-center justify-between px-4 sm:px-6 py-4 flex-shrink-0"
                style={{ background: "rgba(10,22,40,0.7)", borderBottom: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(12px)" }}>
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"
                    className="p-2 rounded-lg text-slate-400 hover:text-white lg:hidden"
                    style={{ background: "rgba(255,255,255,0.05)" }}>
              <Menu size={18} />
            </button>
            <div>
              <h1 className="text-base sm:text-lg font-semibold text-white capitalize" style={{ fontFamily: "Outfit, sans-serif" }}>
                <Breadcrumb pathname={location.pathname} />
              </h1>
              <p className="text-xs text-slate-400 hidden sm:block">AMDiris Electronic Data Capture</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <NotificationBell />
            <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg"
                 style={{ background: "rgba(255,255,255,0.05)" }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
                   style={{ background: "linear-gradient(135deg, #00B4D8, #14B8A6)" }}>
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div>
                <p className="text-sm font-medium text-white leading-none">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize leading-tight">{ROLE_LABELS[user?.role] || user?.role?.replace("_", " ")}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6" data-testid="main-content">
          <MandatoryAgreementGate>
            <Outlet />
          </MandatoryAgreementGate>
        </main>
      </div>
    </div>
  );
}

// Breadcrumb that resolves /studies/:id to "Studies / <study name>"
function Breadcrumb({ pathname }) {
  const studyMatch = matchPath("/studies/:studyId", pathname);
  const [studyName, setStudyName] = useState(() =>
    studyMatch ? studyNameCache.get(studyMatch.params.studyId) : null
  );

  useEffect(() => {
    if (!studyMatch) return;
    const id = studyMatch.params.studyId;
    if (studyNameCache.has(id)) {
      setStudyName(studyNameCache.get(id));
      return;
    }
    let cancelled = false;
    axios.get(`${API}/studies/${id}`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const name = r.data?.name || id;
        studyNameCache.set(id, name);
        setStudyName(name);
      })
      .catch(() => !cancelled && setStudyName(id.slice(0, 8) + "…"));
    return () => { cancelled = true; };
  }, [studyMatch?.params?.studyId]); // eslint-disable-line

  if (studyMatch) {
    return (
      <span data-testid="breadcrumb-study">
        <span className="text-slate-400 font-normal">Studies</span>
        <span className="text-slate-600 font-normal mx-2">/</span>
        <span>{studyName || "Loading…"}</span>
      </span>
    );
  }
  const label = pathname.replace("/", "").replace(/-/g, " ") || "Dashboard";
  return <span>{label}</span>;
}
